import React from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';

// Multibrand-UI Components
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import StyledContainer from '@zg-rentals/multibrand-ui/lib/StyledContainer';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import StarRating from '@zg-rentals/multibrand-ui/lib/StarRating';
import Flag from '@zg-rentals/multibrand-ui/lib/icons/Flag';

import EditReviewResponse from 'app/shared/components/EditReviewResponse';
import ReportReview from 'app/shared/components/ReportReview';

import StringConstants from 'app/shared/constants/StringConstants';
import ReviewsConstants from 'app/shared/constants/ReviewsConstants';

// Stylesheets
import './style.scss';

const propTypes = {
    review: PropTypes.shape({
        id: PropTypes.string.isRequired,
        reviewType: PropTypes.string.isRequired,
        reviewResponseId: PropTypes.string,
        starLevel: PropTypes.number.isRequired,
        reviewMessage: PropTypes.string.isRequired,
        reviewStatus: PropTypes.string.isRequired,
        brand: PropTypes.string.isRequired,
        createdIsoString: PropTypes.string.isRequired,
        responseMessage: PropTypes.string,
        managerReportedStatus: PropTypes.string,
    }).isRequired,
    onSubmitResponse: PropTypes.func.isRequired,
    onReportReview: PropTypes.func.isRequired,
    canEdit: PropTypes.bool.isRequired,
};

const defaultProps = {
};

function formatDate(date) {
    return `${format(new Date(date), 'MMM D, YYYY')}`;
}

class Review extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isResponding: false,
            isReporting: false,
        };
        this.handleToggleResponding = this.handleToggleResponding.bind(this);
        this.handleToggleReporting = this.handleToggleReporting.bind(this);
        this.handleReport = this.handleReport.bind(this);
        this.handleSubmitResponse = this.handleSubmitResponse.bind(this);
    }

    handleSubmitResponse(message) {
        const { id, reviewType, reviewResponseId } = this.props.review;
        return this.props.onSubmitResponse(id, reviewType, message, reviewResponseId)
            .then(() => this.setState({ isResponding: false }));
    }

    handleReport(reason, comment) {
        const { id, reviewType } = this.props.review;
        return this.props.onReportReview(id, reviewType, reason, comment)
            .then(() => this.setState({ isReporting: false }));
    }

    handleToggleResponding() {
        this.setState({ isResponding: !this.state.isResponding });
    }

    handleToggleReporting() {
        if (this.props.review.managerReportedStatus !== ReviewsConstants.REPORT_REVIEW_STATUS.PENDING) {
            this.setState({ isReporting: !this.state.isReporting });
        }
    }

    renderReview() {
        const { review } = this.props;
        const {
            starLevel, reviewMessage, createdIsoString, brand, reviewType,
        } = review;
        return (
            <div>
                <div className="Review-row">
                    <div className="Review-star">
                        <StarRating size={20} stars={5} initialStarValue={starLevel} />
                    </div>
                    <Text emphasize={true}>{ReviewsConstants.REVIEW_TYPES_LABEL[reviewType]}</Text>
                </div>
                {reviewMessage && <Text className="Review-message">{reviewMessage}</Text>}
                <Text className="Review-date">
                    {`Posted on ${StringConstants.FORMATTED_BRAND_NAMES[brand]}: ${formatDate(createdIsoString)}`}
                </Text>
            </div>
        );
    }

    renderReportReviewForm() {
        return (
            <div className="Review-form">
                <ReportReview
                    onCancel={this.handleToggleReporting}
                    onSubmit={this.handleReport}
                />
            </div>
        );
    }

    renderResponseForm() {
        return (
            <div className="Review-form">
                <EditReviewResponse
                    mode="create"
                    onSubmit={this.handleSubmitResponse}
                    onCancel={this.handleToggleResponding}
                />
            </div>
        );
    }

    renderRespondButton() {
        const { responseMessage } = this.props.review;
        if (responseMessage) {
            return null;
        }
        return (
            <div className="Review-button">
                <Button
                    type="primary"
                    onClick={this.handleToggleResponding}
                >
                    Respond
                </Button>
            </div>
        );
    }

    renderReportButton() {
        const { managerReportedStatus } = this.props.review;
        const isPending = managerReportedStatus === ReviewsConstants.REPORT_REVIEW_STATUS.PENDING;
        return (
            <div
                className="Review-report-container"
                onClick={this.handleToggleReporting}
            >
                <Flag
                    className={isPending ? 'Review-report-disabled' : 'Review-report'}
                    size="sm"
                />
            </div>
        );
    }

    render() {
        const { review, canEdit } = this.props;
        const { reviewStatus } = review;
        const { isResponding, isReporting } = this.state;
        const showButtons = canEdit
            && !isReporting
            && !isResponding
            && reviewStatus !== ReviewsConstants.REVIEW_STATUS.OK_STARS_ONLY;
        return (
            <StyledContainer
                className="Review"
                borderColor="default"
                borderFull={true}
                borderWidth="thin"
            >
                <div className="Review-container">
                    {this.renderReview()}
                    {isReporting && this.renderReportReviewForm()}
                    {isResponding && this.renderResponseForm()}
                    {showButtons && this.renderRespondButton()}
                    {showButtons && this.renderReportButton()}
                </div>
            </StyledContainer>
        );
    }
}

Review.propTypes = propTypes;
Review.defaultProps = defaultProps;

export default Review;
